import React, {
  useEffect,
  useMemo,
  useState,
  type FC,
  type ReactNode,
} from 'react';
import { Box, GlobalStyles, Typography } from '@mui/material';
import { motion, AnimatePresence as MotionPresence } from 'framer-motion';

import { useMytaverse } from '@providers/MytaverseProvider';
import { buildCinemaPalette } from '../DashboardContent/MytaStreams/LoadingCinema/palette';
import {
  CINEMA_FONTS_LINK_ID,
  CINEMA_FONTS_URL,
} from '../DashboardContent/MytaStreams/LoadingCinema/constants';

import {
  BRANDING_TRANSITION_DETAIL,
  BRANDING_TRANSITION_FADE_SEC,
  BRANDING_TRANSITION_HEADLINE,
  BRANDING_TRANSITION_HINT,
  BRANDING_TRANSITION_KICKER,
  BRANDING_TRANSITION_STEPS,
} from './constants';
import {
  brandingTransitionGlobalStyles,
  getBrandingTransitionStyles,
} from './styles';
import { useBrandingTransition } from './useBrandingTransition';

const AnimatePresence = MotionPresence as FC<{ children: ReactNode }>;

const BrandingTransitionScreen = () => {
  const { appTheme } = useMytaverse();
  const palette = useMemo(() => buildCinemaPalette(appTheme), [appTheme]);
  const classes = useMemo(
    () => getBrandingTransitionStyles(palette),
    [palette],
  );

  const [stepIdx, setStepIdx] = useState(0);

  useEffect(() => {
    if (document.getElementById(CINEMA_FONTS_LINK_ID)) {
      return;
    }

    const link = document.createElement('link');
    link.id = CINEMA_FONTS_LINK_ID;
    link.rel = 'stylesheet';
    link.href = CINEMA_FONTS_URL;
    document.head.appendChild(link);
  }, []);

  useEffect(() => {
    const timers = BRANDING_TRANSITION_STEPS.slice(1).map((step, index) =>
      setTimeout(() => setStepIdx(index + 1), step.atMs),
    );

    return () => timers.forEach(clearTimeout);
  }, []);

  return (
    <Box sx={classes.root} className="myta-branding-transition">
      <GlobalStyles styles={brandingTransitionGlobalStyles} />
      <Box sx={classes.backdrop} />
      <Box sx={classes.card} data-testid="branding-transition-card">
        <Typography component="span" sx={classes.kicker}>
          {BRANDING_TRANSITION_KICKER}
        </Typography>
        <Typography component="h2" sx={classes.headline}>
          {BRANDING_TRANSITION_HEADLINE}
        </Typography>
        <Typography component="p" sx={classes.detail}>
          {BRANDING_TRANSITION_DETAIL}
        </Typography>
        <Box sx={classes.stepLine}>
          <Box sx={classes.stepDot} />
          <Typography
            component="span"
            sx={classes.stepName}
            data-testid="branding-transition-step"
          >
            {BRANDING_TRANSITION_STEPS[stepIdx].label}
          </Typography>
        </Box>
        <Box sx={classes.barTrack}>
          <Box sx={classes.barFill} />
        </Box>
        <Typography component="p" sx={classes.hint}>
          {BRANDING_TRANSITION_HINT}
        </Typography>
      </Box>
    </Box>
  );
};

/**
 * Covers the map while it is re-dressed for a newly activated configuration.
 *
 * The fading wrapper is keyed by nothing and the screen inside it by the transition id: a second
 * switch inside the window has to restart the steps and the bar, but keying the wrapper would make
 * AnimatePresence cross-fade one full-screen scrim over another — two darkenings stacked, and under
 * jsdom the exiting one never leaves at all.
 */
const BrandingTransition = () => {
  const transitionId = useBrandingTransition();

  return (
    <AnimatePresence>
      {transitionId === null ? null : (
        <motion.div
          key="branding-transition"
          data-testid="branding-transition"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: BRANDING_TRANSITION_FADE_SEC }}
          style={{ position: 'fixed', inset: 0, zIndex: 1400 }}
        >
          <BrandingTransitionScreen key={transitionId} />
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default BrandingTransition;
